import { useMemo, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { cn } from '@/lib/utils';
import { toTableCellText } from '@/shared/lib/table';
import {
  DENSE_TABLE_CELL_CLASS,
  DENSE_TABLE_CONTAINER_CLASS,
  DENSE_TABLE_HEAD_CLASS,
  DENSE_TABLE_HEADER_CLASS,
  DENSE_TABLE_HEADER_ROW_CLASS,
  DENSE_TABLE_ROW_CLASS,
} from '@/shared/ui/primitives';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

import { getLabOrderDisplayName } from '../model/getLabOrderDisplayName';
import { LabOrderDetailsSheet } from './LabOrderDetailsSheet';

function formatTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function humanizeSnakeCase(value: string): string {
  return value.replace(/_/g, ' ');
}

interface CareStateLabOrdersTableProps {
  openOrderId?: string;
  onConsumeOpenOrder?: () => void;
}

export function CareStateLabOrdersTable({
  openOrderId,
  onConsumeOpenOrder,
}: CareStateLabOrdersTableProps) {
  const { careState, status } = useCareStateDomain();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const labOrders = useMemo(() => {
    if (careState === null) return [];
    return [...careState.lab_orders].sort(
      (a, b) => new Date(b.ordered_at).getTime() - new Date(a.ordered_at).getTime()
    );
  }, [careState]);

  const externalSelectedOrder = useMemo(() => {
    if (openOrderId === undefined || openOrderId === '') {
      return null;
    }
    return labOrders.find((order) => order.id === openOrderId) ?? null;
  }, [labOrders, openOrderId]);

  const effectiveSelectedOrderId = selectedOrderId ?? externalSelectedOrder?.id ?? null;
  const selectedOrder = useMemo(() => {
    if (effectiveSelectedOrderId === null) return null;
    return labOrders.find((order) => order.id === effectiveSelectedOrderId) ?? null;
  }, [effectiveSelectedOrderId, labOrders]);
  const isSheetOpen = sheetOpen || externalSelectedOrder !== null;

  if (status === 'loading') {
    return <div className="text-sm text-muted-foreground">Loading lab orders…</div>;
  }

  if (status === 'error') {
    return (
      <div className="text-sm text-destructive">
        Could not load lab orders. Please try refreshing.
      </div>
    );
  }

  if (labOrders.length === 0) {
    return <div className="text-sm text-muted-foreground">No lab orders found.</div>;
  }

  return (
    <>
      <LabOrderDetailsSheet
        open={isSheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) {
            setSelectedOrderId(null);
            onConsumeOpenOrder?.();
          }
        }}
        order={selectedOrder}
      />

      <div className={DENSE_TABLE_CONTAINER_CLASS}>
        <Table>
          <TableHeader className={DENSE_TABLE_HEADER_CLASS}>
            <TableRow className={DENSE_TABLE_HEADER_ROW_CLASS}>
              <TableHead className={DENSE_TABLE_HEAD_CLASS}>Test</TableHead>
              <TableHead className={DENSE_TABLE_HEAD_CLASS}>Ordered</TableHead>
              <TableHead className={DENSE_TABLE_HEAD_CLASS}>Status</TableHead>
              <TableHead className={DENSE_TABLE_HEAD_CLASS}>Interpretation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {labOrders.map((order) => {
              const isSelected = effectiveSelectedOrderId === order.id;
              const testName = getLabOrderDisplayName(order.order_data.value.lab_test);
              const ariaLabel = `Open lab order details for ${testName}`;

              return (
                <TableRow
                  key={order.id}
                  className={cn(
                    DENSE_TABLE_ROW_CLASS,
                    'cursor-pointer',
                    isSelected && 'bg-foreground/[0.06]'
                  )}
                  role="button"
                  tabIndex={0}
                  aria-label={ariaLabel}
                  onClick={() => {
                    setSelectedOrderId(order.id);
                    setSheetOpen(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setSelectedOrderId(order.id);
                    setSheetOpen(true);
                  }}
                >
                  <TableCell className={DENSE_TABLE_CELL_CLASS}>
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="min-w-0 truncate">{testName}</div>
                      <ArrowUpRight
                        className="size-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </div>
                  </TableCell>
                  <TableCell className={DENSE_TABLE_CELL_CLASS}>
                    {formatTimestamp(order.ordered_at)}
                  </TableCell>
                  <TableCell className={cn(DENSE_TABLE_CELL_CLASS, 'capitalize')}>
                    {humanizeSnakeCase(order.status)}
                  </TableCell>
                  <TableCell className={cn(DENSE_TABLE_CELL_CLASS, 'capitalize')}>
                    {toTableCellText(order.interpretation)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
